import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { PublicAgent } from '@magic/schemas/agents';
import styled from 'styled-components';

import { useChannel } from './channel';
import { Messages } from './components/messages';
import { Prompt } from './components/prompt';
import { UseChatHelpers, useChat } from './hooks/use-chat';
import { useTransitionEffect } from './hooks/use-transition-effect';
import { useUTM } from './hooks/use-utm';
import { Reset } from './reset';

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

export interface ChatProps {
  preview?: boolean;
  agent: PublicAgent;
}

export const Chat = forwardRef<UseChatHelpers, ChatProps>(
  ({ agent, preview }, ref) => {
    const channel = useChannel();

    const utm = useUTM({ agent });

    const chat = useChat({
      api: import.meta.env.VITE_CHAT_API_URL,
      agent,
      preview,
      body: { utm },
    });

    useImperativeHandle(ref, () => chat, [chat]);

    useTransitionEffect(
      (lead) => {
        if (lead) {
          channel?.emit('onLeadCaptured', lead);
        }
      },
      chat.lead,
      undefined
    );

    return (
      <Container>
        <Messages messages={chat.messages} isLoading={chat.isLoading} />
        {chat.status === 'finished' ? (
          <Reset reset={chat.reset} />
        ) : (
          <Prompt
            value={chat.input}
            isLoading={chat.isLoading}
            onChange={chat.handleInputChange}
            onSubmit={chat.handleSubmit}
          />
        )}
      </Container>
    );
  }
);
